import { dsDtos } from '@vectronic/openApi-admin';
import { downloadJsonFile } from '@vectronic/util';

/**
 * Schedule types that share the GPS schedule structure.
 * When exporting/importing, all of these are stored as GPS and are
 * interchangeable for type-validation purposes.
 */
export const GPS_FAMILY_TYPES = new Set<dsDtos.Schedule['scheduleType']>([
  'GPS',
  'PROXIMITY',
  'EXTERNAL_SENSOR_RECEIVER',
  'VIRTUAL_FENCE',
  'HIGH_ACTIVITY_GPS',
]);

export interface ScheduleJsonExport {
  version: 1;
  source: 'remote-commands';
  exportedAt: string;
  schedule: dsDtos.Schedule;
}

export interface ScheduleJsonImportResult {
  schedule?: dsDtos.Schedule;
  errorMessage?: string;
}

export function buildScheduleJsonExport(schedule: dsDtos.Schedule): ScheduleJsonExport {
  const { cameraScheduleRules, rules, userDefined, ...rest } = schedule;

  const scheduleType = GPS_FAMILY_TYPES.has(rest.scheduleType) ? 'GPS' : rest.scheduleType;

  return {
    version: 1,
    source: 'remote-commands',
    exportedAt: new Date().toISOString(),
    schedule: {
      ...rest,
      scheduleType,
      ...(rules?.length ? { rules } : {}),
      ...(cameraScheduleRules?.length ? { cameraScheduleRules } : {}),
    },
  };
}

export function downloadScheduleJsonFile(schedule: dsDtos.Schedule, name?: string) {
  downloadJsonFile(
    buildScheduleJsonFilePrefixFromName(name, schedule.scheduleType),
    buildScheduleJsonExport(schedule),
  );
}

export function buildScheduleJsonFilePrefix(scheduleType: dsDtos.Schedule['scheduleType']) {
  return sanitizeScheduleJsonFilePrefix(scheduleType);
}

export function buildScheduleJsonFilePrefixFromName(
  name: string | undefined,
  scheduleType: dsDtos.Schedule['scheduleType'],
) {
  if (!name?.trim()) {
    return buildScheduleJsonFilePrefix(scheduleType);
  }

  return sanitizeScheduleJsonFilePrefix(`${name}-${scheduleType}`);
}

function sanitizeScheduleJsonFilePrefix(value: string) {
  const normalized = value.trim().replace(/[^a-zA-Z0-9-_]+/g, '-');
  return normalized.replace(/-+/g, '-').replace(/^-|-$/g, '') || 'schedule';
}

export function extractScheduleFromJsonExport(payload: unknown): dsDtos.Schedule | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const exportPayload = payload as Partial<ScheduleJsonExport>;
  if (
    exportPayload.version === 1 &&
    exportPayload.source === 'remote-commands' &&
    exportPayload.schedule?.scheduleType
  ) {
    return exportPayload.schedule;
  }

  const rawSchedule = payload as Partial<dsDtos.Schedule>;
  if (rawSchedule.scheduleType) {
    return rawSchedule as dsDtos.Schedule;
  }

  return undefined;
}

export async function importScheduleJsonFromInputEvent(
  event: Event,
  expectedType: dsDtos.Schedule['scheduleType'],
): Promise<ScheduleJsonImportResult> {
  const input = event.target as HTMLInputElement | null;
  const file = input?.files?.[0];

  if (!file) {
    return {};
  }

  try {
    const parsed = JSON.parse(await file.text());
    const schedule = extractScheduleFromJsonExport(parsed);

    if (!schedule) {
      return { errorMessage: 'Invalid schedule JSON file.' };
    }

    const typeMismatch =
      GPS_FAMILY_TYPES.has(expectedType) && GPS_FAMILY_TYPES.has(schedule.scheduleType)
        ? false
        : schedule.scheduleType !== expectedType;

    if (typeMismatch) {
      return {
        errorMessage: `Imported schedule type "${schedule.scheduleType}" does not match "${expectedType}".`,
      };
    }

    return { schedule };
  } catch {
    return { errorMessage: 'Unable to import schedule JSON file.' };
  } finally {
    if (input) {
      input.value = '';
    }
  }
}
